import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { DualLangInputComponent } from '../../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { WorkflowConfigurationDetailsComponent } from '../../workflow-configuration/workflow-configuration-details/workflow-configuration-details.component';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';
import { ReflectionListComponent } from '../../reflections/reflection-list/reflection-list.component';
import { ReflectionScopeEnum } from '../../reflections/enums/reflection-scope.enum';
import { SectionHeaderComponent } from '../../../../../common/components/section-header/section-header.component';
import { CustomWorkflowService } from '../custom-workflow.service';
import {
  CustomWorkflow,
  CustomWorkflowApiError,
  CustomWorkflowStep,
  FieldAccess,
  Localized,
  localized,
  localizedGroup,
  rawValueSignal,
} from '../custom-workflow.model';

@Component({
  selector: 'lib-custom-workflow-step-editor',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    DualLangInputComponent,
    ButtonComponent,
    ValidationMessageComponent,
    WorkflowConfigurationDetailsComponent,
    SectionHeaderComponent,
    ReflectionListComponent,
  ],
  templateUrl: './custom-workflow-step-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomWorkflowStepEditorComponent implements OnInit {
  readonly service = inject(CustomWorkflowService);
  /** The workflow.reflections flag: off hides everything reflection-related, on custom and built-in workflows alike. */
  protected readonly reflectionsEnabled = signal(
    inject(FeatureFlagsService).getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.reflections
    )
  ).asReadonly();
  private readonly formService = inject(FormService);
  private readonly transloco = inject(TranslocoService);

  workflow = input.required<CustomWorkflow>();
  step = input.required<CustomWorkflowStep>();
  readonly = input<boolean>(false);
  changed = output<void>();
  deleted = output<void>();
  apiError = output<CustomWorkflowApiError>();

  isSaving = signal(false);
  lang = this.transloco.getActiveLang();
  readonly FieldAccess = FieldAccess;
  protected readonly Scope = ReflectionScopeEnum;
  readonly accessLevels = Object.values(FieldAccess);
  fields = computed(() => this.workflow().sections.flatMap(s => s.fields));
  canDelete = computed(
    () => !this.workflow().locked && !this.step().is_initiation
  );
  fieldRows = computed(() =>
    this.fields().map(field => ({ key: field.key, title: field.label }))
  );
  embeddedWorkflowId = computed(() => String(this.workflow().id));

  form = new FormGroup({
    name: localizedGroup(),
  });
  fieldAccessForm = new FormGroup<Record<string, FormControl<FieldAccess>>>({});
  private readonly initial = signal('');
  private readonly nameValue = rawValueSignal(this.form);
  readonly accessValue = rawValueSignal(this.fieldAccessForm);

  configDetails = viewChild(WorkflowConfigurationDetailsComponent);
  /** Unsaved name/field-access edits, or unsaved approver edits in the embedded configuration form. */
  dirty = computed(
    () =>
      JSON.stringify([this.nameValue(), this.accessValue()]) !==
        this.initial() || !!this.configDetails()?.isFormChanged()
  );

  ngOnInit() {
    const step = this.step();
    this.form.patchValue({ name: step.translations.name });
    for (const field of this.fields()) {
      const access: FieldAccess = step.editable_fields.includes(field.key)
        ? FieldAccess.Edit
        : step.hidden_fields.includes(field.key)
          ? FieldAccess.Hidden
          : FieldAccess.View;
      this.fieldAccessForm.addControl(
        field.key,
        new FormControl<FieldAccess>(access, { nonNullable: true })
      );
    }
    if (this.readonly()) {
      this.form.disable();
      this.fieldAccessForm.disable();
    }
    this.initial.set(this.snapshot());
  }

  private snapshot(): string {
    return JSON.stringify([
      this.form.getRawValue(),
      this.fieldAccessForm.getRawValue(),
    ]);
  }

  private label(value: Localized) {
    return localized(value, this.lang);
  }

  save() {
    const step = this.step();
    if (!step.is_initiation) {
      this.formService.syncDualLangControls(this.form);
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
    }
    const raw = this.form.getRawValue();
    const body = step.is_initiation
      ? { field_access: this.fieldAccessForm.getRawValue() }
      : { name: raw.name, field_access: this.fieldAccessForm.getRawValue() };

    this.isSaving.set(true);
    this.service.updateStep(this.workflow().id, step.id, body).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.initial.set(this.snapshot());
        // Rename the embedded approver header in place; a reload would drop unsaved approver edits.
        if (!step.is_initiation)
          this.configDetails()
            ?.workFlowStepsList()
            .forEach(s => (s.name = this.label(raw.name)));
        this.changed.emit();
      },
      error: (error: CustomWorkflowApiError) => {
        this.isSaving.set(false);
        if (error?.errors) this.formService.addControlErrors(error, this.form);
        this.apiError.emit(error);
      },
    });
  }

  remove() {
    this.service.deleteStep(this.workflow().id, this.step().id).subscribe({
      next: () => this.deleted.emit(),
      error: e => this.apiError.emit(e),
    });
  }
}
